import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export interface SearchableOption {
  code: string;
  name: string;
}

export interface SearchableSelectUI {
  bg?: string;
  border?: string;
  text?: string;
  textMuted?: string;
  primary?: string;
  primaryLight?: string;
  surfaceHover?: string;
  radius?: string;
  shadow?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (code: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Open the menu upward (e.g. when near the bottom of a modal). */
  openUp?: boolean;
  ui?: SearchableSelectUI;
}

// Defaults map to the management app's v2 CSS variables so existing forms need no theming.
const DEFAULT_UI: Required<SearchableSelectUI> = {
  bg: 'var(--v2-surface)',
  border: 'var(--v2-border)',
  text: 'var(--v2-text)',
  textMuted: 'var(--v2-text-muted)',
  primary: 'var(--v2-primary)',
  primaryLight: 'var(--v2-primary-light)',
  surfaceHover: 'var(--v2-surface-hover)',
  radius: 'var(--radius-sm)',
  shadow: '0 4px 12px rgba(0,0,0,0.15)',
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  openUp = false,
  ui,
}) => {
  const c = { ...DEFAULT_UI, ...(ui || {}) };
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.code === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)
    );
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Focus the search box when opened
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: c.bg,
    border: `1px solid ${c.border}`,
    borderRadius: c.radius,
    fontSize: '0.875rem',
    color: selected ? c.text : c.textMuted,
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    opacity: disabled ? 0.6 : 1,
  };

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    ...(openUp ? { bottom: '100%', marginBottom: '4px' } : { top: '100%', marginTop: '4px' }),
    backgroundColor: c.bg,
    border: `1px solid ${c.border}`,
    borderRadius: c.radius,
    boxShadow: c.shadow,
    zIndex: 30,
    overflow: 'hidden',
  };

  const searchWrapStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    borderBottom: `1px solid ${c.border}`,
  };

  const searchInputStyle: React.CSSProperties = {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '0.875rem',
    color: c.text,
  };

  const listStyle: React.CSSProperties = {
    maxHeight: '220px',
    overflowY: 'auto',
    padding: '4px',
  };

  const itemStyle = (isSel: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '8px 10px',
    border: 'none',
    backgroundColor: isSel ? c.primaryLight : 'transparent',
    color: isSel ? c.primary : c.text,
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '0.875rem',
    borderRadius: c.radius,
    fontWeight: isSel ? 500 : 400,
    display: 'block',
  });

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
          setQuery('');
        }}
        style={buttonStyle}
      >
        <span>{selected?.name || placeholder}</span>
        <ChevronDown
          size={16}
          strokeWidth={1.5}
          color={c.textMuted}
          style={{ transition: 'transform 0.15s ease', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
        />
      </button>

      {open && (
        <div style={menuStyle} onClick={(e) => e.stopPropagation()}>
          <div style={searchWrapStyle}>
            <Search size={15} strokeWidth={1.5} color={c.textMuted} />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              style={searchInputStyle}
            />
          </div>
          <div style={listStyle}>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px', fontSize: '0.8125rem', color: c.textMuted }}>No matches</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.code}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(o.code);
                    setOpen(false);
                    setQuery('');
                  }}
                  style={itemStyle(o.code === value)}
                  onMouseOver={(e) => {
                    if (o.code !== value) e.currentTarget.style.backgroundColor = c.surfaceHover;
                  }}
                  onMouseOut={(e) => {
                    if (o.code !== value) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {o.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
